import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Video, Zap, ChevronDown, ChevronUp, ArrowRight } from 'lucide-react';
import { supabase } from '../lib/supabase';

const features = [
  {
    icon: Mail,
    title: 'Daily emails for 21 days',
    description:
      'Each day walks you through the Refinement System questions and gives you one clear action to take.',
  },
  {
    icon: Video,
    title: 'Short video guides from Joey',
    description:
      'Keep you on track and make the practice stick — no long courses, just focused guidance.',
  },
  {
    icon: Zap,
    title: 'Your values-based AI prompt',
    description:
      'Personalized to your three core values so you can keep the momentum going long after day 21.',
  },
];

const faqs = [
  {
    q: 'How much time does this take each day?',
    a: 'About ten minutes. The Refinement System is designed to be simple and fast so there is no excuse not to show up.',
  },
  {
    q: 'Do I need to have completed the Clarity Tool first?',
    a: 'Yes. Your three core values are the foundation of the challenge. If you have not identified them yet you can do that on the core values page.',
  },
  {
    q: 'What area should I pick for the challenge?',
    a: 'Pick the one where you have felt the most stuck. Money, fitness, relationships, a creative project. It does not matter what it is. The Refinement System works on any area.',
  },
  {
    q: 'What happens after 21 days?',
    a: 'You will have a proven daily practice you can apply to any area of your life. Many people book an Alignment Call after the challenge to map out how to take it further.',
  },
];

function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between text-left px-5 py-4 bg-white hover:bg-blue-50 transition-colors duration-150"
      >
        <span className="font-semibold text-[#004060] text-sm leading-snug pr-4">{q}</span>
        {open ? (
          <ChevronUp className="w-4 h-4 text-[#236caf] shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-[#236caf] shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-5 pb-4 pt-1 bg-blue-50">
          <p className="text-gray-700 text-sm leading-relaxed">{a}</p>
        </div>
      )}
    </div>
  );
}

function CTAButton({ isLoading, onClick }: { isLoading: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      disabled={isLoading}
      className="inline-flex items-center gap-2 bg-[#236caf] hover:bg-[#004060] disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold py-3.5 px-8 rounded-xl transition-colors duration-200 text-base shadow-md"
    >
      {isLoading ? (
        <>
          <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          Redirecting to checkout...
        </>
      ) : (
        <>
          Start the Challenge for $21
          <ArrowRight className="w-4 h-4" />
        </>
      )}
    </button>
  );
}

export function ChallengeOverview() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleCheckout = async () => {
    setIsLoading(true);
    setError('');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not logged in');

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/challenge-checkout`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await res.json();

      if (data?.error === 'already_purchased') {
        navigate('/dashgrowthloop');
        return;
      }

      if (!res.ok) throw new Error(data?.error || 'Failed to start checkout');

      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Something went wrong';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-blue-50">
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#004060] to-[#236caf] text-white">
        <div className="max-w-2xl mx-auto px-6 py-16 text-center space-y-6">
          <div className="inline-flex items-center gap-2 bg-white/20 text-white text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full">
            21 Day Challenge
          </div>
          <h1 className="text-4xl font-bold leading-tight">
            Become Unstoppable in 21 Days
          </h1>
          <p className="text-blue-100 text-lg leading-relaxed max-w-xl mx-auto">
            You have your values. Now it is time to put them to work.
          </p>
          <p className="text-blue-100 leading-relaxed max-w-xl mx-auto">
            The 21 Day Unstoppable Challenge uses the Refinement System to help you break through
            one stuck area of your life in just 21 days. One daily practice. One powerful shift.
            One area where you stop saying "I'll get to that" and start making real progress.
          </p>
          <div className="pt-2">
            <CTAButton isLoading={isLoading} onClick={handleCheckout} />
          </div>
          {error && (
            <p className="text-red-300 text-sm">{error}</p>
          )}
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-12 space-y-12">
        {/* What You Get */}
        <section className="space-y-5">
          <h2 className="text-2xl font-bold text-[#004060]">Here is what you get</h2>
          <div className="space-y-4">
            {features.map(({ icon: Icon, title, description }) => (
              <div
                key={title}
                className="flex gap-4 bg-white rounded-xl p-5 shadow-sm border border-gray-100"
              >
                <div className="shrink-0 w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                  <Icon className="w-5 h-5 text-[#236caf]" />
                </div>
                <div>
                  <p className="font-semibold text-[#004060] mb-1">{title}</p>
                  <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="space-y-4">
          <h2 className="text-2xl font-bold text-[#004060]">Frequently Asked Questions</h2>
          <div className="space-y-2">
            {faqs.map((faq) => (
              <FAQItem key={faq.q} q={faq.q} a={faq.a} />
            ))}
          </div>
        </section>

        {/* Closing CTA */}
        <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 text-center space-y-4">
          <h3 className="text-xl font-bold text-[#004060]">Ready to stop getting stuck?</h3>
          <p className="text-gray-600 text-sm leading-relaxed max-w-sm mx-auto">
            One area. 21 days. One shift that changes how you show up.
          </p>
          <CTAButton isLoading={isLoading} onClick={handleCheckout} />
          {error && <p className="text-red-500 text-sm">{error}</p>}
        </section>
      </div>
    </div>
  );
}
